import axiosNest from '../api/axiosNest';
import { toErrorMessage } from '../admin/userOffers/orderActionsApi';
import { fromDateTimeLocalValue } from '../shared/clientTime';
import type { ContractorRef } from './preOrderContractorApi';

/**
 * Wprowadzanie prezleceń z ręki (odpowiada `pre-order.controller.ts`).
 *
 * Dopóki ekstrakcja z maila (W3) nie działa, to jedyna droga wejścia zlecenia
 * do systemu. Akceptacja jest osobnym wywołaniem, bo to jawna decyzja
 * człowieka — z niej powstaje zlecenie z naszym numerem.
 */

export interface PreOrderInput {
  companyId?: number | string | null;
  clientName?: string | null;
  /**
   * Numer zamówienia od klienta (16.10.2026, odpowiedzi Marathonu/Euvic,
   * pyt. 27) — VIA nada własny numer, a po tym rozpozna zlecenie import z TMS.
   */
  customerOrderNumber?: string | null;
  relation?: string | null;
  loadingCity?: string | null;
  unloadingCity?: string | null;
  /** Kraj (ISO alfa-2, np. PL) i kod pocztowy punktów — z nich punkt na mapie (06.10.2026). */
  loadingCountry?: string | null;
  loadingPostalCode?: string | null;
  unloadingCountry?: string | null;
  unloadingPostalCode?: string | null;
  loadingDate?: string | null;
  unloadingDate?: string | null;
  /** Wymagany typ pojazdu jak w TMS; „+FORKLIFT” = drugi plik dla VIA (pyt. 39e). */
  requiredVehicleType?: string | null;
  cargo?: string | null;
  weight?: number | string | null;
  pallets?: number | string | null;
  price?: number | string | null;
  currency?: string | null;
  responsibleDept?: string | null;
  /**
   * Kontrahent z kartoteki (fala B, 21.10.2026, `preOrderContractor`) — do
   * API idzie sam `contractorId`; `contractor` to wybór do pokazania
   * w formularzu. Brak klucza = bez zmiany.
   */
  contractorId?: number | null;
  contractor?: ContractorRef | null;
  /**
   * Źródło ustalenia przy poprawce (21.10.2026, `preOrderAgreementSource`):
   * rodzaj (dane z API) i opis; brak = bez źródła.
   */
  agreementSource?: { kind: string; note?: string } | null;
}

export interface PreOrder extends PreOrderInput {
  id: number;
  status: string;
  missingFields: string[];
  acceptedBy: string | null;
  acceptedAt: string | null;
}

export interface CreatedOrder {
  id: number;
  orderNumber: string | null;
}

/**
 * Daty z formularza (czas polski bez strefy) idą do API z przesunięciem
 * Warszawy — chwila, której serwer nie odczyta źle (`shared/clientTime.ts`).
 */
export function toApiInput(input: PreOrderInput): PreOrderInput {
  // Kontrahent do pokazania nie idzie do API — tylko jego `contractorId`.
  const { contractor, ...rest } = input;
  void contractor;
  const out: PreOrderInput = { ...rest };
  if ('loadingDate' in input) out.loadingDate = fromDateTimeLocalValue(input.loadingDate);
  if ('unloadingDate' in input) out.unloadingDate = fromDateTimeLocalValue(input.unloadingDate);
  return out;
}

export async function createPreOrder(
  input: PreOrderInput,
): Promise<PreOrder> {
  const { data } = await axiosNest.post<PreOrder>('/pre-orders', toApiInput(input));
  return data;
}

export async function updatePreOrder(
  id: number | string,
  input: PreOrderInput,
): Promise<PreOrder> {
  const { data } = await axiosNest.put<PreOrder>(`/pre-orders/${id}`, toApiInput(input));
  return data;
}

/** Jawna akceptacja — powstaje zlecenie z naszym numerem. */
export async function acceptPreOrder(
  id: number | string,
): Promise<CreatedOrder> {
  const { data } = await axiosNest.post<CreatedOrder>(
    `/pre-orders/${id}/accept`,
  );
  return data;
}

export { toErrorMessage };
